import React, { FC, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { DropdownContent, DropdownDetachedTop, DropdownDivider, DropdownItemBody, DropdownItemText, DropdownList, DropdownListItem } from './dropdown';
import { DropdownPlacement, useDropdown } from '../hooks/useDropdown';

type DropdownActionsOrientation = 'LEFT' | 'RIGHT';
type DropdownActionsVerticalOrientation = 'TOP' | 'BOTTOM';

interface DropdownActionsProps {
    header: string | ReactNode;
    detachedTop?: ReactNode;
    sections?: ReactNode[][];
    items?: ReactNode[];
    orientation?: DropdownActionsOrientation;
    verticalOrientation?: DropdownActionsVerticalOrientation;
    placement?: DropdownPlacement;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    maxHeight?: string;
    surfacePadding?: string;
    zIndex?: number;
    matchTriggerWidth?: boolean;
    overlay?: boolean;
    overlayBackground?: string;
}

interface DropdownActionItemProps {
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    selected?: boolean;
    danger?: boolean;
    testId?: string;
}

const DropdownActionsContainer = styled.div`
    display: inline-flex;
    align-items: center;
`;

const DropdownActionsTrigger = styled.div`
    display: inline-flex;
    align-items: center;
    user-select: none;
`;

const resolvePlacement = (
    placement?: DropdownPlacement,
    orientation?: DropdownActionsOrientation,
    verticalOrientation?: DropdownActionsVerticalOrientation
): DropdownPlacement => {
    if (placement) return placement;

    if (verticalOrientation === 'BOTTOM') {
        return orientation === 'RIGHT' ? 'top-end' : 'top-start';
    }

    return orientation === 'RIGHT' ? 'bottom-end' : 'bottom-start';
};

const normalizeSections = (sections?: ReactNode[][], items?: ReactNode[]) => {
    const rawSections = sections ?? (items ? [items] : []);
    return rawSections.map((section) => section.filter(Boolean)).filter((section) => section.length > 0);
};

// Use DropdownActionItem for clickable entries inside DropdownActions.
// Set danger for destructive actions, selected for the active item, and disabled when the action is temporarily unavailable.
export const DropdownActionItem: FC<DropdownActionItemProps> = ({ children, onClick, disabled, selected, danger, testId }) => {
    return (
        <DropdownListItem type='button' $active={selected} $danger={danger} disabled={disabled} onClick={() => !disabled && onClick?.()} data-testid={testId}>
            <DropdownItemBody>
                <DropdownItemText>{children}</DropdownItemText>
            </DropdownItemBody>
        </DropdownListItem>
    );
};

// Use DropdownActions for row or toolbar action menus, including three-dots menus.
// Example:
// <DropdownActions
//     header={<BasicButton type='button' $size='small' $type='icon' aria-label={T._('Actions', 'Admin')}><Icon><VerticalThreePoint /></Icon></BasicButton>}
//     items={[<DropdownActionItem key='edit' onClick={handleEdit}>{T._('Edit', 'Admin')}</DropdownActionItem>]}
//     orientation='RIGHT'
// />
// Use sections instead of items when groups need dividers; use placement only when LEFT/RIGHT and TOP/BOTTOM are not specific enough.
// Use detachedTop for a primary action that must sit in its own card above the dropdown panel.
export const DropdownActions: FC<DropdownActionsProps> = ({
    header,
    detachedTop,
    sections,
    items,
    orientation,
    verticalOrientation,
    placement,
    width,
    minWidth = '300px',
    maxWidth,
    maxHeight,
    surfacePadding = '0',
    zIndex,
    matchTriggerWidth,
    overlay,
    overlayBackground
}) => {
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();
    const visibleSections = useMemo(() => normalizeSections(sections, items), [items, sections]);

    return (
        <DropdownActionsContainer>
            <DropdownActionsTrigger
                onClick={(e) => {
                    e.stopPropagation();
                    isOpen ? closeDropdown() : openDropdown(e.currentTarget, resolvePlacement(placement, orientation, verticalOrientation));
                }}
            >
                {header}
            </DropdownActionsTrigger>
            {isOpen && visibleSections.length > 0 && (
                <Dropdown
                    width={width}
                    minWidth={minWidth}
                    maxWidth={maxWidth}
                    maxHeight={maxHeight}
                    surfacePadding={surfacePadding}
                    zIndex={zIndex}
                    matchTriggerWidth={matchTriggerWidth}
                    overlay={overlay}
                    overlayBackground={overlayBackground}
                    detachedTop={
                        detachedTop && (
                            <DropdownDetachedTop
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeDropdown();
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                {detachedTop}
                            </DropdownDetachedTop>
                        )
                    }
                >
                    <DropdownContent
                        onClick={(e) => {
                            e.stopPropagation();
                            closeDropdown();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <DropdownList>
                            {visibleSections.map((section, sectionIndex) => (
                                <React.Fragment key={`section_${sectionIndex}`}>
                                    {sectionIndex > 0 && <DropdownDivider />}
                                    {section.map((item, itemIndex) => (
                                        <React.Fragment key={`item_${sectionIndex}_${itemIndex}`}>{item}</React.Fragment>
                                    ))}
                                </React.Fragment>
                            ))}
                        </DropdownList>
                    </DropdownContent>
                </Dropdown>
            )}
        </DropdownActionsContainer>
    );
};
